<template>
  <div>
    <dx-grid
      :url="url"
      :columns="columns"
      :title="title"
      :onRowClicked="onRowClicked"
      navigationPath="onRowClicked"
      navigationField="onRowClicked"
      ref="grid"
    ></dx-grid>

    <v-dialog v-model="detailsDialog" max-width="920" persistent scrollable>
      <v-card class="food-app-dialog">
        <!-- ── Header bar ── -->
        <div class="fad-header">
          <div class="fad-header-left">
            <div class="fad-header-name">
              <span class="fad-header-id"
                >#{{ getApplicationID(clickedApplication) }}</span
              >
              {{ clickedApplication.firstName }}
              {{ clickedApplication.lastName }}
            </div>
            <div class="fad-header-ref">
              Ref: {{ clickedApplication.referenceNumber || "-" }} &nbsp;|&nbsp;
              Submitted: {{ formatDateTime(clickedApplication.submittedAt) }}
            </div>
          </div>
          <div class="fad-header-right">
            <v-chip
              class="fad-status-chip"
              color="white"
              :text-color="
                getStatusChipColor(clickedApplication.externalApiStatus)
              "
              small
            >
              <v-icon
                left
                small
                :color="
                  getStatusChipColor(clickedApplication.externalApiStatus)
                "
              >
                {{ getStatusIcon(clickedApplication.externalApiStatus) }}
              </v-icon>
              {{ getStatusText(clickedApplication.externalApiStatus) }}
            </v-chip>
            <div
              v-if="clickedApplication.externalApiForwardedAt"
              class="fad-forwarded-at"
            >
              {{ formatDateTime(clickedApplication.externalApiForwardedAt) }}
              <span v-if="clickedApplication.externalApiForwardedBy">
                by {{ clickedApplication.externalApiForwardedBy }}
              </span>
            </div>
          </div>
        </div>

        <div class="fad-body">
          <!-- ── Two-column detail sections ── -->
          <v-layout row wrap class="fad-sections">
            <!-- Left column -->
            <v-flex xs12 sm6 class="fad-col pr-3">
              <div class="fad-section">
                <div class="fad-section-title">Personal Info</div>
                <div class="fad-row">
                  <span class="fad-label">Date of Birth</span>
                  <span class="fad-val">{{
                    formatDate(clickedApplication.dateOfBirth)
                  }}</span>
                </div>
                <div class="fad-row">
                  <span class="fad-label">Medicaid ID</span>
                  <span class="fad-val">{{
                    clickedApplication.medicaidID || "-"
                  }}</span>
                </div>
                <div class="fad-row">
                  <span class="fad-label">Address</span>
                  <span class="fad-val">{{
                    getAddress(clickedApplication)
                  }}</span>
                </div>
              </div>

              <div class="fad-section">
                <div class="fad-section-title">Contact</div>
                <div class="fad-row">
                  <span class="fad-label">Home Phone</span>
                  <span class="fad-val">{{
                    clickedApplication.homePhone || "-"
                  }}</span>
                </div>
                <div class="fad-row">
                  <span class="fad-label">Cell Phone</span>
                  <span class="fad-val">{{
                    clickedApplication.cellPhone || "-"
                  }}</span>
                </div>
                <div class="fad-row">
                  <span class="fad-label">Email</span>
                  <span class="fad-val">{{
                    clickedApplication.email || "-"
                  }}</span>
                </div>
              </div>

              <div class="fad-section mb-2">
                <div class="fad-section-title">Employment &amp; Benefits</div>
                <div class="fad-row">
                  <span class="fad-label">Employed</span>
                  <span class="fad-val">{{
                    formatBoolean(clickedApplication.isEmployed)
                  }}</span>
                </div>
                <div class="fad-row">
                  <span class="fad-label">Spouse Employed</span>
                  <span class="fad-val">{{
                    formatBoolean(clickedApplication.isSpouseEmployed)
                  }}</span>
                </div>
                <div class="fad-row">
                  <span class="fad-label">WIC</span>
                  <span class="fad-val">{{
                    formatBoolean(clickedApplication.hasWIC)
                  }}</span>
                </div>
                <div class="fad-row">
                  <span class="fad-label">SNAP</span>
                  <span class="fad-val">{{
                    formatBoolean(clickedApplication.hasSNAP)
                  }}</span>
                </div>
              </div>
            </v-flex>

            <!-- Right column -->
            <v-flex xs12 sm6 class="fad-col">
              <div class="fad-section">
                <div class="fad-section-title">Health</div>
                <div class="fad-row">
                  <span class="fad-label">Pregnant</span>
                  <span class="fad-val">{{
                    formatBoolean(clickedApplication.isPregnant)
                  }}</span>
                </div>
                <div class="fad-row" v-if="clickedApplication.isPregnant">
                  <span class="fad-label">Due Date</span>
                  <span class="fad-val">{{
                    formatDate(clickedApplication.dueDate)
                  }}</span>
                </div>
                <div class="fad-row">
                  <span class="fad-label">Post Partum</span>
                  <span class="fad-val">{{
                    formatBoolean(clickedApplication.isPostPartum)
                  }}</span>
                </div>
                <div class="fad-row">
                  <span class="fad-label">Had Miscarriage</span>
                  <span class="fad-val">{{
                    formatBoolean(clickedApplication.hadMiscarriage)
                  }}</span>
                </div>
                <div class="fad-row" v-if="clickedApplication.infantName">
                  <span class="fad-label">Infant</span>
                  <span class="fad-val">
                    {{ clickedApplication.infantName }}
                    ({{ formatDate(clickedApplication.infantDateOfBirth) }})
                  </span>
                </div>
                <div class="fad-row" v-if="clickedApplication.infantMedicaidID">
                  <span class="fad-label">Infant Medicaid</span>
                  <span class="fad-val">{{
                    clickedApplication.infantMedicaidID
                  }}</span>
                </div>
              </div>

              <div class="fad-section">
                <div class="fad-section-title">Additional Info</div>
                <div class="fad-row">
                  <span class="fad-label">Dietary Restrictions</span>
                  <span class="fad-val">{{
                    clickedApplication.dietaryRestrictions || "-"
                  }}</span>
                </div>
                <div class="fad-row">
                  <span class="fad-label">Cooking Supplies</span>
                  <span class="fad-val">{{
                    formatBoolean(clickedApplication.needsCookingSupplies)
                  }}</span>
                </div>
                <div class="fad-row">
                  <span class="fad-label">Refrigerator</span>
                  <span class="fad-val">{{
                    formatBoolean(clickedApplication.needsRefrigerator)
                  }}</span>
                </div>
                <div class="fad-row">
                  <span class="fad-label">Microwave</span>
                  <span class="fad-val">{{
                    formatBoolean(clickedApplication.needsMicrowave)
                  }}</span>
                </div>
                <div class="fad-row" v-if="clickedApplication.comments">
                  <span class="fad-label">Comments</span>
                  <span class="fad-val">{{ clickedApplication.comments }}</span>
                </div>
              </div>

              <div
                class="fad-section"
                v-if="clickedApplication.externalApiResponse"
              >
                <div class="fad-section-title">API Response</div>
                <div class="fad-row">
                  <span class="fad-label">Response</span>
                  <span class="fad-val">{{
                    clickedApplication.externalApiResponse
                  }}</span>
                </div>
              </div>
            </v-flex>
          </v-layout>

          <div
            v-if="canUpdateApplication(clickedApplication)"
            class="fad-update-panel"
          >
            <div class="fad-update-header">
              <v-icon small color="#2e7d32" class="mr-2"
                >mdi-pencil-circle</v-icon
              >
              <span>Upstate Application</span>
            </div>
            <div
              v-if="clickedApplication.updatedAt || clickedApplication.updatedBy"
              class="fad-update-meta"
            >
              Last update:
              <span v-if="clickedApplication.updatedAt">
                {{ formatDateTime(clickedApplication.updatedAt) }}
              </span>
              <span v-if="clickedApplication.updatedBy">
                by {{ clickedApplication.updatedBy }}
              </span>
            </div>
            <v-layout row wrap class="fad-update-content">
              <v-flex xs12>
                <v-select
                  v-model="updateForm.status"
                  :items="statusOptions"
                  label="Status"
                  dense
                  outlined
                  hide-details
                ></v-select>
              </v-flex>
              <v-flex xs12>
                <v-textarea
                  v-model="updateForm.userNote"
                  label="Note"
                  dense
                  outlined
                  rows="2"
                  hide-details
                ></v-textarea>
              </v-flex>
            </v-layout>
            <div class="fad-update-actions">
              <v-btn
                small
                color="success"
                :loading="updatingApplication"
                @click="updateApplication"
              >
                Update Application
              </v-btn>
            </div>
          </div>

          <!-- ── Members table ── -->
          <div class="fad-members-header">
            <span class="fad-section-title mb-0">Application Members</span>
            <span class="fad-members-count">{{ members.length }}</span>
            <v-progress-circular
              v-if="loadingMembers"
              indeterminate
              color="success"
              size="18"
              width="2"
              class="ml-2"
            ></v-progress-circular>
          </div>

          <div class="fad-members-wrap">
            <table class="fad-members-table">
              <thead>
                <tr>
                  <th style="width:44px">#</th>
                  <th>Full Name</th>
                  <th style="width:140px">Date of Birth</th>
                  <th style="width:130px">Medicaid ID</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="member in members"
                  :key="member.memberID"
                  class="fad-member-row"
                >
                  <td class="fad-td-center">{{ member.memberOrder }}</td>
                  <td class="fad-td-name">{{ member.fullName }}</td>
                  <td>{{ formatDate(member.dateOfBirth) }}</td>
                  <td>{{ member.medicaidID }}</td>
                </tr>
                <tr v-if="!loadingMembers && !members.length">
                  <td colspan="4" class="fad-td-empty">
                    No members found for this application.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ── Footer actions ── -->
        <div class="fad-footer">
          <v-btn color="default" dark @click="closeDetailsDialog">Close</v-btn>
          <v-btn
            v-if="
              canForward(
                clickedApplication.externalApiStatus,
                clickedApplication
              )
            "
            :loading="forwarding"
            color="success"
            @click="openForwardDialog"
          >
            <v-icon left small>mdi-send</v-icon>
            Forward To UJO
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirmForwardDialog" max-width="500" persistent>
      <v-card class="pa-3">
        <v-card-title class="headline pl-0 pt-0">
          Confirm Forward
        </v-card-title>
        <div class="body-1">
          Are you sure you want to forward application
          <strong>#{{ getApplicationID(clickedApplication) }}</strong>
          to UJO?
        </div>
        <v-card-actions class="pr-0 pt-4 pb-0 mr-0">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="confirmForwardDialog = false">
            Cancel
          </v-btn>
          <v-btn
            :loading="forwarding"
            color="success"
            @click="confirmForwardApplication"
          >
            Yes, Forward
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";

export default {
  computed: {},
  created() {
    const isOnlyUpstate = this.hasRole("Food Assistance Applications Upstate");

    this.url = "UjoFoodAssistance?isOnlyUpstate=" + isOnlyUpstate;
  },
  data: () => ({
    title: "Food Assistance Applications",
    url: "",
    detailsDialog: false,
    confirmForwardDialog: false,
    loadingMembers: false,
    forwarding: false,
    updatingApplication: false,
    clickedApplication: {},
    members: [],
    statusOptions: ["PENDING", "COMPLETED"],
    updateForm: {
      status: "",
      userNote: "",
    },
    columns: [
      {
        field: "externalApiStatus",
        isIcon: true,
        template: "icon",
        alignment: "center",
        visible: true,
        allowFiltering: false,
        allowHeaderFiltering: false,
        allowHiding: false,
        allowSearch: false,
        cssClass: "width-25",
        headerFilter: {
          allowSearch: false,
          groupInterval: undefined,
          height: undefined,
          searchMode: "contains",
          width: undefined,
        },
        icon: (value, row) => {
          if (row.status == "PENDING") return "mdi-clock-outline";
          if (row.status == "COMPLETED") return "mdi-check-circle";

          if (value === 200) return "mdi-check-circle";
          if (value === 409) return "mdi-alert-circle";

          return "mdi-clock-outline";
        },
        iconColor: (value, row) => {
          if (row.status == "PENDING") return "#fdb400";
          if (row.status == "COMPLETED") return "success";

          if (value === 200) return "success";
          if (value === 409) return "#f35123";

          return "#fdb400";
        },
      },
      {
        label: "Status",
        field: "status",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "External API Status",
        field: "externalApiStatus",
        type: "number",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
        formatFn: (value) => {
          if (value === 200) return "Success (200)";
          if (value === 409) return "Duplicate (409)";

          return "Not Submitted";
        },
      },
      {
        label: "Entered By",
        field: "enteredBy",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Application ID",
        field: "applicationID",
        type: "number",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Reference Number",
        field: "referenceNumber",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "submittedAt",
        label: "Submitted At",
        type: "date",
        format: "MM/dd/yyyy hh:mm:ss a",
        sortOrder: "desc",
        sortIndex: 0,
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "First Name",
        field: "firstName",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Last Name",
        field: "lastName",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "dateOfBirth",
        label: "Date Of Birth",
        type: "date",
        format: "MM/dd/yyyy",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Medicaid ID",
        field: "medicaidID",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Street Address",
        field: "streetAddress",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Apt",
        field: "apt",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Building Code",
        field: "buildingCode",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Floor Number",
        field: "floorNumber",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Building Directions",
        field: "buildingDirections",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "City",
        field: "city",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "State",
        field: "state",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Zip Code",
        field: "zipCode",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Home Phone",
        field: "homePhone",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Cell Phone",
        field: "cellPhone",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Email",
        field: "email",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Is Employed",
        field: "isEmployed",
        type: "boolean",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Is Spouse Employed",
        field: "isSpouseEmployed",
        type: "boolean",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Has WIC",
        field: "hasWIC",
        type: "boolean",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Has SNAP",
        field: "hasSNAP",
        type: "boolean",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Is Pregnant",
        field: "isPregnant",
        type: "boolean",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "dueDate",
        label: "Due Date",
        type: "date",
        format: "MM/dd/yyyy",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Had Miscarriage",
        field: "hadMiscarriage",
        type: "boolean",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "miscarriageDate",
        label: "Miscarriage Date",
        type: "date",
        format: "MM/dd/yyyy",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Is Post Partum",
        field: "isPostPartum",
        type: "boolean",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Infant Name",
        field: "infantName",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "infantDateOfBirth",
        label: "Infant Date Of Birth",
        type: "date",
        format: "MM/dd/yyyy",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Infant Medicaid ID",
        field: "infantMedicaidID",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Has Health Home Care Management",
        field: "hasHealthHomeCareManagement",
        type: "boolean",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Has Substance Use Disorder",
        field: "hasSubstanceUseDisorder",
        type: "boolean",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Has Serious Mental Illness",
        field: "hasSeriousMentalIllness",
        type: "boolean",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Has Child Chronic Condition",
        field: "hasChildChronicCondition",
        type: "boolean",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Has Other Health Condition",
        field: "hasOtherHealthCondition",
        type: "boolean",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Dietary Restrictions",
        field: "dietaryRestrictions",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Needs Cooking Supplies",
        field: "needsCookingSupplies",
        type: "boolean",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Needs Refrigerator",
        field: "needsRefrigerator",
        type: "boolean",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Needs Microwave",
        field: "needsMicrowave",
        type: "boolean",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Comments",
        field: "comments",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Certified By Applicant",
        field: "certifiedByApplicant",
        type: "boolean",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Ujo Application ID",
        field: "ujoApplicationID",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Ujo Entity ID",
        field: "ujoEntityID",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "updatedAt",
        label: "Updated At",
        type: "date",
        format: "MM/dd/yyyy hh:mm:ss a",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Updated By",
        field: "updatedBy",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "User Note",
        field: "userNote",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "External API Response",
        field: "externalApiResponse",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        field: "externalApiForwardedAt",
        label: "External API Forwarded At",
        type: "date",
        format: "MM/dd/yyyy hh:mm:ss a",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "External API Forwarded By",
        field: "externalApiForwardedBy",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
    ],
  }),
  methods: {
    formatDate(value) {
      if (!value) return "-";

      return moment(value).format("MM/DD/YYYY");
    },
    formatDateTime(value) {
      if (!value) return "-";

      return moment(value).format("MM/DD/YYYY hh:mm:ss a");
    },
    formatBoolean(value) {
      if (value === null || value === undefined) return "-";

      return value ? "Yes" : "No";
    },
    getStatusIcon(value) {
      if (value === 200) return "mdi-check-circle";
      if (value === 409) return "mdi-alert-circle";

      return "mdi-clock-outline";
    },
    getStatusIconColor(value) {
      if (value === 200) return "success";
      if (value === 409) return "#f35123";

      return "#fdb400";
    },
    getStatusChipColor(value) {
      if (value === 200) return "#2e7d32";
      if (value === 409) return "#c62828";

      return "rgb(253, 180, 0)";
    },
    getStatusText(value) {
      if (value === 200) return "Success (200)";
      if (value === 409) return "Duplicate (409)";

      return "Not Submitted";
    },
    isAdmin() {
      const isAdmin = this.$store.getters["authentication/isAdmin"];

      if (!isAdmin || isAdmin == "false") return false;

      return true;
    },
    hasRole(roleName) {
      const roles = Array.isArray(this.$store.state.authentication.roles)
        ? this.$store.state.authentication.roles
        : this.$store.state.authentication.roles
        ? this.$store.state.authentication.roles.split(",")
        : [];

      return roles
        .map((role) => (role || "").trim())
        .includes((roleName || "").trim());
    },
    canForward(value, application) {
      if (value !== null && value !== undefined) return false;
      if (this.canUpdateApplication(application)) return false;

      return this.isAdmin() || this.hasRole("Food Assistance Forward To UJO");
    },
    getAddress(application) {
      const parts = [];

      if (application.streetAddress) parts.push(application.streetAddress);
      if (application.apt) parts.push("Apt " + application.apt);
      if (application.city) parts.push(application.city);
      if (application.state) parts.push(application.state);
      if (application.zipCode) parts.push(application.zipCode);

      return parts.length ? parts.join(", ") : "-";
    },
    getApplicationID(application) {
      if (
        application.applicationID !== null &&
        application.applicationID !== undefined
      ) {
        return application.applicationID;
      }

      if (
        application.ApplicationID !== null &&
        application.ApplicationID !== undefined
      ) {
        return application.ApplicationID;
      }

      if (
        application.applicationId !== null &&
        application.applicationId !== undefined
      ) {
        return application.applicationId;
      }

      return "";
    },
    closeDetailsDialog() {
      this.detailsDialog = false;
      this.confirmForwardDialog = false;
      this.members = [];
      this.clickedApplication = {};
      this.updateForm.status = "";
      this.updateForm.userNote = "";
    },
    canUpdateApplication(application) {
      const city = ((application && application.city) || "")
        .trim()
        .toLowerCase();

      return city !== "brooklyn";
    },
    initializeUpdateForm(application) {
      this.updateForm.status = (application && application.status) || "";
      this.updateForm.userNote = (application && application.userNote) || "";
    },
    async updateApplication() {
      const applicationId = this.getApplicationID(this.clickedApplication);
      if (!applicationId) return;

      try {
        this.updatingApplication = true;
        await this.$http.post("api/UjoFoodAssistance/Update", {
          applicationID: applicationId,
          status: this.updateForm.status,
          userNote: this.updateForm.userNote,
        });

        this.clickedApplication.status = this.updateForm.status;
        this.clickedApplication.userNote = this.updateForm.userNote;

        this.$notify({
          text: "Application updated successfully",
          group: "notification",
          type: "success",
        });

        if (this.$refs.grid) {
          this.$refs.grid.reloadGrid();
        }

        this.closeDetailsDialog();
      } catch (e) {
        this.$notify({
          text: "Failed to update application",
          group: "notification",
          type: "error",
        });
      } finally {
        this.updatingApplication = false;
      }
    },
    openForwardDialog() {
      if (
        !this.canForward(
          this.clickedApplication.externalApiStatus,
          this.clickedApplication
        )
      ) {
        return;
      }

      this.confirmForwardDialog = true;
    },
    async confirmForwardApplication() {
      this.confirmForwardDialog = false;

      await this.forwardApplication();
    },
    async onRowClicked(row) {
      this.clickedApplication = Object.assign({}, row);
      this.initializeUpdateForm(this.clickedApplication);
      this.detailsDialog = true;

      await this.loadMembers();
    },
    async loadMembers() {
      const applicationId = this.getApplicationID(this.clickedApplication);

      if (!applicationId) {
        this.members = [];

        return;
      }

      try {
        this.loadingMembers = true;

        const response = await this.$http.get(
          "api/UjoFoodAssistance/GetMembersByApplicationID?applicationId=" +
            applicationId
        );

        this.members = Array.isArray(response) ? response : [];
      } catch (e) {
        this.members = [];
        this.$notify({
          text: "Failed to load members",
          group: "notification",
          type: "error",
        });
      } finally {
        this.loadingMembers = false;
      }
    },
    async forwardApplication() {
      const applicationId = this.getApplicationID(this.clickedApplication);

      if (!applicationId) return;

      try {
        this.forwarding = true;

        const userName = this.$store.state.authentication.user;
        const isLocalhost = window.location.hostname === "localhost";
        const baseUrl = isLocalhost
          ? "https://localhost:44316"
          : "https://ujo-food-assistance-cxcng0fsd5hxauea.canadacentral-01.azurewebsites.net";
        const response = await this.$http.post(
          baseUrl + "/api/applications/" + applicationId + "/forward",
          { userName: userName }
        );

        const status =
          response.ExternalApiStatus !== undefined
            ? response.ExternalApiStatus
            : response.externalApiStatus;
        const forwardedAt =
          response.ExternalApiForwardedAt !== undefined
            ? response.ExternalApiForwardedAt
            : response.externalApiForwardedAt;
        const forwardedBy =
          response.ExternalApiForwardedBy !== undefined
            ? response.ExternalApiForwardedBy
            : response.externalApiForwardedBy;
        const ujoApplicationID =
          response.UjoApplicationID !== undefined
            ? response.UjoApplicationID
            : response.ujoApplicationID;
        const externalApiResponse =
          response.ExternalApiResponse !== undefined
            ? response.ExternalApiResponse
            : response.externalApiResponse;

        this.clickedApplication.externalApiStatus = status;
        this.clickedApplication.externalApiForwardedAt = forwardedAt;
        this.clickedApplication.externalApiForwardedBy = forwardedBy;
        this.clickedApplication.ujoApplicationID = ujoApplicationID;
        this.clickedApplication.externalApiResponse = externalApiResponse;

        if (status === 200) {
          this.$notify({
            text: "Application forwarded successfully",
            group: "notification",
            type: "success",
          });
        } else {
          let errorMessage = "Forward failed";

          if (status === 409) {
            errorMessage =
              "Duplicate — an application with this name and Medicaid ID already exists";
          } else if (externalApiResponse) {
            try {
              const parsed = JSON.parse(externalApiResponse);
              if (parsed && parsed.message) {
                errorMessage = parsed.message;
              }
            } catch (parseErr) {
              errorMessage = externalApiResponse;
            }
          }

          this.$notify({
            text: errorMessage,
            group: "notification",
            type: "error",
          });
        }

        if (this.$refs.grid) {
          this.$refs.grid.reloadGrid();
        }
      } catch (e) {
        this.$notify({
          text: "Failed to forward application",
          group: "notification",
          type: "error",
        });
      } finally {
        this.forwarding = false;
      }
    },
  },
};
</script>

<style>
/* ── Dialog shell ── */
.food-app-dialog {
  background: #fff;
  border-radius: 10px;
  display: flex !important;
  flex-direction: column;
  max-height: 92vh;
  overflow: hidden;
}

/* ── Header bar ── */
.fad-header {
  display: flex;
  flex-shrink: 0;
  align-items: flex-start;
  justify-content: space-between;
  background: #4caf50;
  color: #fff;
  padding: 20px 24px 16px;
}
.fad-header-id {
  font-size: 18px;
  font-weight: 700;
  margin-right: 8px;
  opacity: 0.85;
}
.fad-header-name {
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: baseline;
  gap: 2px;
}
.fad-header-ref {
  font-size: 12px;
  opacity: 0.75;
  margin-top: 6px;
}
.fad-header-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}
.fad-status-chip.v-chip {
  font-weight: 600;
  letter-spacing: 0.3px;
}
.fad-forwarded-at {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.75);
  text-align: right;
}

/* ── Body ── */
.fad-body {
  padding: 20px 24px 0;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

/* ── Two-column layout ── */
.fad-sections {
  margin-bottom: 4px;
}
.fad-col {
  padding-bottom: 4px;
}

/* ── Section ── */
.fad-section {
  margin-bottom: 18px;
}
.fad-section-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #2e7d32;
  border-bottom: 2px solid #e8f5e9;
  padding-bottom: 4px;
  margin-bottom: 8px;
}

/* ── Label / value row ── */
.fad-row {
  display: flex;
  align-items: flex-start;
  padding: 5px 0;
  border-bottom: 1px solid #f5f5f5;
  font-size: 13.5px;
  line-height: 1.4;
}
.fad-row:last-child {
  border-bottom: none;
}
.fad-label {
  flex: 0 0 148px;
  color: #757575;
  padding-right: 10px;
}
.fad-val {
  flex: 1;
  color: #212121;
  font-weight: 500;
  word-break: break-word;
}

/* ── Update section ── */
.fad-update-panel {
  margin: 0px 0 18px;
  padding: 14px 16px 16px;
  border: 2px solid #a5d6a7;
  border-radius: 10px;
  background: #f3fbf4;
  width: calc(50% - 8px);
  min-width: 320px;
}
.fad-update-header {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #1b5e20;
  margin-bottom: 2px;
}
.fad-update-content {
  margin-top: 10px;
}
.fad-update-meta {
  margin-top: 4px;
  color: #2e7d32;
  font-size: 12px;
  font-weight: 500;
}
.fad-update-actions {
  display: flex;
  justify-content: flex-start;
  margin-top: 12px;
}
@media (max-width: 959px) {
  .fad-update-panel {
    width: 100%;
    min-width: 0;
  }
}

/* ── Members section ── */
.fad-members-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  margin-top: 6px;
}
.fad-members-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #4caf50;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border-radius: 10px;
  min-width: 24px;
  height: 20px;
  padding: 0 7px;
}
.fad-members-wrap {
  border: 1px solid #dcedc8;
  border-radius: 8px;
  overflow: hidden;
}
.fad-members-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  font-size: 13.5px;
}
.fad-members-table thead th {
  background: #f1f8e9;
  color: #33691e;
  font-weight: 700;
  font-size: 11.5px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  padding: 10px 16px;
  text-align: left;
  border-bottom: 2px solid #dcedc8;
  white-space: nowrap;
}
.fad-members-table tbody td {
  padding: 10px 16px;
  color: #212121;
  border-bottom: 1px solid #f1f8e9;
  white-space: nowrap;
}
.fad-members-table tbody tr:last-child td {
  border-bottom: none;
}
.fad-member-row:hover td {
  background: #f9fbe7;
}
.fad-td-center {
  text-align: center;
  color: #757575;
  font-size: 13px;
}
.fad-td-name {
  font-weight: 600;
}
.fad-td-empty {
  text-align: center;
  padding: 20px 16px !important;
  color: #9e9e9e;
  font-style: italic;
}

/* ── Footer ── */
.fad-footer {
  display: flex;
  flex-shrink: 0;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding: 14px 24px 16px;
  border-top: 1px solid #f0f0f0;
  background: #fff;
}
</style>
